import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Lock, Shield, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const inputStyle = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

export default function AdminLoginPage() {
  useSEO({
    title: 'Admin Sign In | Acodia',
    description: 'Administrator sign-in for the Acodia platform.',
    path: '/admin/login',
    robots: 'noindex,nofollow',
  });
  const { loginStep1, loginStep2, admin, token } = useAdminAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const digitRefs = useRef([]);

  useEffect(() => {
    if (admin && token) navigate('/admin', { replace: true });
  }, [admin, token, navigate]);

  useEffect(() => {
    if (step === 2) digitRefs.current[0]?.focus();
  }, [step]);

  const renderError = (err) => {
    if (!err) return null;
    if (err.status === 429) return { kind: 'lockout', message: err.message || 'Account temporarily locked.' };
    if (err.status === 403) return { kind: 'ip', message: err.message || 'Access denied from this IP address.' };
    return { kind: 'creds', message: err.message || 'Invalid email or password.' };
  };
  const errInfo = renderError(error);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await loginStep1(email.trim().toLowerCase(), password);
      setStep(2);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (code) => {
    setError(null);
    setLoading(true);
    try {
      await loginStep2(email.trim().toLowerCase(), code);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err);
      setDigits(['', '', '', '', '', '']);
      digitRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (idx, value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 1);
    if (!cleaned && value !== '') return;
    const next = [...digits];
    next[idx] = cleaned;
    setDigits(next);
    if (cleaned && idx < 5) digitRefs.current[idx + 1]?.focus();
    if (cleaned && idx === 5 && next.every((d) => d !== '')) {
      submitTotp(next.join(''));
    }
  };

  const handleDigitKey = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      digitRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) digitRefs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) digitRefs.current[idx + 1]?.focus();
  };

  const handleDigitPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, 5);
    digitRefs.current[focusIdx]?.focus();
    if (pasted.length === 6) submitTotp(pasted);
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--bg)' }}
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield size={18} style={{ color: 'var(--a)' }} />
          <span className="font-display text-2xl font-bold" style={{ color: 'var(--white)' }}>acodia</span>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded"
            style={{ color: 'var(--a-bright)', background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
          >
            admin
          </span>
        </div>

        <div
          className="p-8 rounded-lg"
          style={{ background: 'var(--l1)', border: '1px solid var(--b1)' }}
        >
          <h1 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--white)' }}>
            {step === 1 ? 'Administrator Sign In' : 'Two-Factor Verification'}
          </h1>
          <p className="font-body text-xs mb-6" style={{ color: 'var(--ts)' }}>
            {step === 1 ? 'Enter your credentials to continue.' : `Enter the 6-digit code for ${email}.`}
          </p>

          {errInfo && (
            <div
              data-testid={`admin-login-error-${errInfo.kind}`}
              className="flex items-start gap-2 p-3 rounded mb-4"
              style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}
            >
              <AlertCircle size={14} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 1 }} />
              <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{errInfo.message}</p>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Email</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                  <Input
                    data-testid="admin-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@acodia.co.uk"
                    className="font-body text-sm h-11 rounded-[3px] pl-9"
                    style={inputStyle}
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs mb-1.5 block" style={{ color: 'var(--ts)' }}>Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--te)' }} />
                  <Input
                    data-testid="admin-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter administrator password"
                    className="font-body text-sm h-11 rounded-[3px] pl-9 pr-10"
                    style={inputStyle}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--te)' }}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="w-full btn-shimmer font-body font-medium text-sm h-11 rounded-[3px]"
                style={{ background: 'var(--a)', color: '#fff', opacity: loading ? 0.6 : 1 }}
              >
                {loading ? 'Verifying…' : 'Continue'}
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2" data-testid="admin-totp-boxes">
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (digitRefs.current[i] = el)}
                    data-testid={`admin-totp-digit-${i}`}
                    value={d}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleDigitKey(i, e)}
                    onPaste={i === 0 ? handleDigitPaste : undefined}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className="w-11 h-12 text-center font-mono text-lg rounded-[3px] focus:outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.025)',
                      border: d ? '1px solid var(--a)' : '1px solid var(--b1)',
                      color: 'var(--white)',
                      caretColor: 'var(--a)',
                    }}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setStep(1); setDigits(['', '', '', '', '', '']); setError(null); }}
                  className="font-body text-xs"
                  style={{ color: 'var(--tm)' }}
                >
                  ← Back
                </button>
                {loading && (
                  <span className="font-mono text-xs" style={{ color: 'var(--a)' }}>Verifying…</span>
                )}
              </div>
            </div>
          )}
        </div>

        <p
          data-testid="admin-login-security-note"
          className="text-center mt-4 font-mono text-[11px] tracking-wide"
          style={{ color: 'var(--te)' }}
        >
          Max 5 attempts · IP logged · Session expires in 30 minutes
        </p>
      </div>
    </div>
  );
}
